import { DatePipe, formatDate, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, LOCALE_ID, OnInit } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { APP_SETTINGS, UtilityService } from '@services';
import { UserInfo } from 'src/app/core/models/userInfo';
import { Table } from 'src/app/shared/components/table/table.component.types';
import { IPage } from 'src/app/shared/utils/page';
import { getUserBrandName } from 'src/app/shared/utils/utils';
import { SpinnerService } from '../../../core/services/spinner/spinner.service';
import { UserProfileService } from '../../user-profile/user-profile.service';
import { FormsModule } from '@angular/forms';
import { TableComponent } from '../../../shared/components/table/table.component';

@Component({
    selector: 'app-show-users',
    templateUrl: './show-users.component.html',
    styleUrls: ['./show-users.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        RouterLink,
        FormsModule,
        NgClass,
        TableComponent,
    ],
})
export class ShowUsersComponent implements OnInit {

  private readonly locale = inject(LOCALE_ID);

  filterQuery: string = '';
  currentPageUsers: IPage<UserInfo>
  table: Table = {
    headers: ['Name', 'Username', 'Email', 'Address', 'Subscription', 'Type'],
    numberOfElements: 10,
    rows: [],
  }
  makeName: string = 'BMW';
  APP_SETTINGS: any;
  constructor(
    private readonly userProfileService: UserProfileService,
    private readonly spinnerService: SpinnerService,
    private readonly router: Router,
    private readonly date: DatePipe,
    private readonly cdr: ChangeDetectorRef) { }

  ngOnInit() {
    this.APP_SETTINGS = APP_SETTINGS;
    this.getAllUsersByMakeName(0)
  }

  getAllUsersByMakeName(pageNumber = 0) {
    this.spinnerService.show('bmwSpinner');
    this.userProfileService.getAllUsers(this.makeName, pageNumber, 10, this.filterQuery).subscribe(usersData => {
      this.spinnerService.hide('bmwSpinner');
      this.currentPageUsers = usersData
      this.updateTable(usersData)
      this.cdr.markForCheck();
    }, error => {
      this.table.errorMessage = error?.error?.message ?? 'Unable to fetch users';
      this.spinnerService.hide('bmwSpinner');
      this.cdr.markForCheck();
    });
  }

  private updateTable(usersData: IPage<UserInfo>) {
    this.table.rows = usersData?.content?.map(user => {
      return {
        id: user.id,
        cells: [
          { value: user?.firstName + " " + user?.lastName },
          { value: user?.qID },
          { value: user?.email, cssClass: 'break-all' },
          { value: user?.address1 + (user?.address2 ? ", " + user?.address2 : ""), cssClass: 'break-all' },
          { value: "expires in " + (user?.accountExpiryDate ? formatDate(user.accountExpiryDate, 'short', this.locale) : '--/--/--') },
          { value: user?.userType?.userTypeName },
        ],
      }
    })
    this.table.number = usersData?.number
    this.table.totalElements = usersData?.totalElements
    this.table.errorMessage = this.table.rows?.length > 0 ? '' : 'No users found'
  }

  deleteUser(id: string) {
    console.log('User ' + id + ' deleted')
  }

  editUser(id: number) {
    let user: UserInfo = this.currentPageUsers.content.find(user => user.id === id)
    console.log('Editing user: ', user)
    if (user) {
      this.router.navigate(["/manage-user", "edit"], {
        queryParams:
        {
          "id": user.id,
          "qID": user.qID,
          "firstName": user.firstName,
          "lastName": user.lastName,
          "email": user.email,
          "phone": user.phone,
          "address1": user.address1,
          "address2": user.address2,
          "city": user.city,
          "state": user.state,
          "zipCode": user.zipCode,
          "companyName": user.companyName,
          "isActive": user.isActive,
          "isCompanionAccount": user.isCompanionAccount,
          "isBMW": user.isBMW,
          "isMINI": user.isMINI,
          "isROLLSROYCE": user.isROLLSROYCE,
          "accountExpiryDate": user.accountExpiryDate,
          "userTypeId": user.userType.id,
          "userTypeName": user.userType.userTypeName,
          "userTypeDesc": user.userType.userTypeDesc,
          "createdOn": user.createdOn,
          "lastSignOn": user.lastSignOn,
          "brandType": getUserBrandName(user)
        }
      });
    }
  }

  pageNumChange(event) {
    this.getAllUsersByMakeName(event ?? 0)
    UtilityService.scrollPageToStart()
  }

  exportToCSV() {
    this.spinnerService.show('bmwSpinner');
    this.userProfileService.getAllUsers(this.makeName, 0, this.currentPageUsers.totalElements, this.filterQuery).subscribe(
      allUsers => {
        this.generateCSV(allUsers.content);
        this.spinnerService.hide('bmwSpinner');
      },
      error => {
        this.spinnerService.hide('bmwSpinner');
        alert('Error exporting users: ' + (error?.error?.message || 'Unknown error'));
      }
    );
  }

  private generateCSV(allUsers: UserInfo[]) {
    if (!allUsers || allUsers.length === 0) {
      alert('No users available to export');
      return;
    }

    const separator = ',';
    const csvRows = [];

    csvRows.push(`sep=${separator}`);

    const headers = [
      'Name',
      'Email',
      'Address 1',
      'Username',
      'Subscription Expiration Date',
      'Brand'
    ];
    csvRows.push(headers.join(separator));

    allUsers.forEach(user => {
      if (!user) {
        return;
      }

      const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
      const brandType = getUserBrandName(user);
      const expiryDate = user.accountExpiryDate ? formatDate(user.accountExpiryDate, 'short', this.locale) : '';

      const row = [
        this.escapeCsvValue(fullName),
        this.escapeCsvValue(user.email || ''),
        this.escapeCsvValue(user.address1 || ''),
        this.escapeCsvValue(user.qID || ''),
        this.escapeCsvValue(expiryDate),
        this.escapeCsvValue(brandType)
      ];
      csvRows.push(row.join(separator));
    });

    const csvContent = csvRows.join('\r\n');

    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'application/vnd.ms-excel' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').substring(0, 19);
    link.setAttribute('download', `Users_Report_${this.makeName}_${timestamp}.xls`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  private escapeCsvValue(value: string): string {
    if (value === null || value === undefined) {
      return '';
    }
    const stringValue = String(value);
    if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
      return '"' + stringValue.replace(/"/g, '""') + '"';
    }
    return stringValue;
  }

}
